import React from 'react';
import styled from 'styled-components/macro';
import { MdKeyboardBackspace } from 'react-icons/md';
import {
  Search,
  Dropdown,
  LoadingIndicator,
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
  NewModal,
} from 'lib/components';
import { EmptyDrops } from '../components';
import { DropItem } from 'lib/api/droplr/types';
import {
  BOARD_PRIVACY,
  DROPTYPE_OPTIONS,
  DropType,
  SortFields,
} from 'lib/const/Droplr';
import { useGetDrops } from 'lib/api/droplr/getDrops';
import { Button } from 'react-covideo-common';
import { Flex } from 'lib/components/styles/layout';
import {
  ParagraphNormalBold,
  ParagraphSmall,
  ParagraphSmallBold,
} from 'lib/components/styles/typography';
import { useTranslation } from 'react-i18next';

type ThumbnailProps = {
  image: string;
};

const Content = styled.div`
  min-width: 528px;
  width: auto;
`;

const Item = styled(Flex)`
  background: ${({ theme }) => theme.colors.white[100]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[20]};
  box-shadow: 0px 4px 12px ${({ theme }) => theme.colors.black[5]};
  border-radius: 4px;
  box-sizing: border-box;
  padding-right: 16px;
`;

const Thumbnail = styled.div<ThumbnailProps>`
  width: 96px;
  height: 100%;
  flex-shrink: 0;
  cursor: pointer;
  background-image: url(${props => props.image});
  background-repeat: no-repeat;
  background-size: cover;
  background-color: ${({ theme }) => theme.colors.white[100]};
`;

type Props = {
  setShowFileListModal: React.Dispatch<React.SetStateAction<boolean>>;
  setThumbnailImageUrl: React.Dispatch<React.SetStateAction<string>>;
  setIsCustomImage: React.Dispatch<React.SetStateAction<boolean>>;
  editorImage: string;
  setEditorImage: React.Dispatch<React.SetStateAction<string>>;
};

export const FileListModal = ({
  setShowFileListModal,
  setIsCustomImage,
  setEditorImage,
}: Props) => {
  const { t } = useTranslation();
  const initialDropParams = {
    page: 0,
    size: 10,
    search: '',
    sort: SortFields[3].key,
    order: SortFields[3].order,
    type: DropType.IMAGE,
    dropType: DROPTYPE_OPTIONS.OWNED,
  };

  const [filter, setFilter] = React.useState(initialDropParams);
  const {
    data: dropsResult,
    isLoading: loading,
    refetch,
  } = useGetDrops(filter);
  const { drops: dropsData, count } = dropsResult || { drops: [], count: 0 };
  const drops = dropsData.filter(
    (drop: DropItem) => drop.privacy === BOARD_PRIVACY.PUBLIC
  );

  const handleSelectFileClick = (drop: DropItem) => {
    setIsCustomImage(true);
    setEditorImage(drop.previewMedium || drop.content || '');
    setShowFileListModal(false);
  };

  const onPaginationChange = ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setFilter(prev => ({ ...prev, size: newSize, page: newPage }));
  };

  const getCurrentSortFieldValue = () => {
    return (
      SortFields.find(
        field => field.key === filter.sort && field.order === filter.order
      ) || SortFields[0]
    );
  };
  return (
    <>
      <NewModal
        hideCloseButtonInHeader
        closeModal={() => setShowFileListModal(false)}
        hideHeader
        style={{
          overlay: {
            background: 'transparent',
          },
          content: {
            width: '630px',
          },
        }}
      >
        <div>
          <Button
            variant='transparent'
            icon={<MdKeyboardBackspace />}
            text={t('Back')}
            onClick={() => {
              setShowFileListModal(false);
            }}
          />
          <Content>
            {loading && <LoadingIndicator isLoading={loading} height='500px' />}
            {!loading && (
              <>
                {(filter.search || !!drops.length) && (
                  <div>
                    <ParagraphSmall>
                      Select an Image from Library
                    </ParagraphSmall>
                    <Flex
                      width='100%'
                      justifyContent='space-between'
                      flexDirection='row'
                    >
                      <div style={{ width: '320px' }}>
                        <Search
                          placeholder={`Search files...`}
                          onSearch={(query: string) => {
                            setFilter(prev => ({
                              ...prev,
                              page: 0,
                              search: query.toLowerCase(),
                            }));
                          }}
                          prevSearch={filter.search}
                        />
                      </div>
                      <div style={{ display: 'flex' }}>
                        <Dropdown
                          value={getCurrentSortFieldValue()}
                          onChange={newValue => {
                            setFilter(prev => ({
                              ...prev,
                              sort: newValue.key,
                              order: newValue.order,
                            }));
                          }}
                          options={SortFields}
                          dropdownHeight={450}
                          height={40}
                          width={200}
                          creatable={false}
                        />
                      </div>
                    </Flex>
                    {!!drops.length && (
                      <>
                        <div style={{ height: '420px', overflowY: 'scroll' }}>
                          <Flex
                            gap='16px'
                            flexDirection='column'
                            margin='16px 0'
                          >
                            {drops.map((drop: DropItem) => {
                              return (
                                <Item
                                  key={drop.code}
                                  flexDirection='row'
                                  height='64px'
                                  gap='16px'
                                  alignItems='center'
                                >
                                  <Flex
                                    flexDirection='row'
                                    gap='16px'
                                    height='100%'
                                    alignItems='center'
                                  >
                                    <Thumbnail image={drop.previewSmall} />
                                    <ParagraphNormalBold
                                      ellipsis
                                      style={{
                                        whiteSpace: 'nowrap',
                                        width: 350,
                                      }}
                                    >
                                      {drop.title}
                                    </ParagraphNormalBold>
                                  </Flex>

                                  <Button
                                    size='small'
                                    text={t('Select File')}
                                    onClick={() => {
                                      handleSelectFileClick(drop);
                                    }}
                                  />
                                </Item>
                              );
                            })}
                          </Flex>
                        </div>
                        {count > 0 && (
                          <TableContextProvider
                            total={count}
                            initSize={filter.size}
                            initPage={filter.page}
                            onChange={onPaginationChange}
                          >
                            <TableFooter>
                              <TablePaginationNew />
                              <TablePaginationSizeNew globalName='drops_list' />
                            </TableFooter>
                          </TableContextProvider>
                        )}
                      </>
                    )}
                    {!drops.length && (
                      <Flex
                        height='486px'
                        alignItems='center'
                        justifyContent='center'
                      >
                        <ParagraphSmallBold>
                          There are no images with "{filter.search}" in the
                          title
                        </ParagraphSmallBold>
                      </Flex>
                    )}
                  </div>
                )}
                {!filter.search && !drops.length && (
                  <EmptyDrops
                    hideVideoLibrary={true}
                    excludeFilesTab={true}
                    onUploadDropModalClosed={refetch}
                  />
                )}
              </>
            )}
          </Content>
        </div>
      </NewModal>
    </>
  );
};
